import { Col, Row } from "../components/layout.jsx";
import {
  Form,
  FormCheckbox,
  FormField,
  FormInput,
  FormInputAppend,
  FormSelect,
  Yup,
  useForm,
  useFormAction,
  yupResolver,
} from "../components/forms.jsx";
import {
  Headline,
  ImageHeader,
  MainContent,
} from "../templates/common/Components.jsx";
import { color, font, styled, theme } from "../util/style.js";
import { getAmountExclTax, roundToCents } from "../../common/price.js";
import { useCallback, useRef, useState } from "react";

import BudgetHeader from "../components/BudgetHeader.jsx";
import { Button } from "../components/typography.jsx";
import Container from "../templates/common/Container.jsx";
import LoggedInTemplate from "../templates/LoggedInTemplate.jsx";
import { Redirect } from "react-router-dom";
import _pick from "lodash/pick";
import { formatCurrency } from "../../common/currency.js";
import header from "../img/bandeau_formations.jpg";
import { paths } from "../services/router.js";
import { useSelfMetaData } from "../services/self.js";
import { useTrainingCourses } from "../services/trainingCourses.js";
import { useTrainingVoucherCreate } from "../services/trainingVouchers.js";

const ScrollRef = styled.div`
  position: relative;
  top: -100px;
`;

const Panel = styled.div`
  background: white;
  border-radius: ${theme("border-radius")};
  padding: ${theme("padding-container")};
  margin-bottom: 36px;
  hr {
    margin: 32px 0;
  }
`;

const GreyContent = styled.div`
  background: ${color("bg-light")};
  border-radius: ${theme("border-radius")};
  padding: ${theme("padding-container")};
`;

const PanelTitle = styled.div`
  ${font("headline5")}
  text-align: center;
`;

const PanelSubtitle = styled.div`
  ${font("paragraph")}
  text-align: center;
`;

const Buttons = styled.div`
  padding: 0 ${theme("padding-container")};
  display: flex;
  justify-content: space-between;
`;

const AmountCapMessage = styled.p`
  ${font("small")}
  color: ${color("input-error")};
  margin: 0 0 25px;
`;

const TrainingCourseOptionCode = styled.span`
  color: ${color("text-dark")};
  font-weight: bold;
`;

const TrainingCourseOptionDescription = styled.div`
  color: ${color("text-grey")};
  font-style: italic;
  font-size: 0.85em;
`;

// L'option active de react-select est sur fond bleu plein : ses couleurs de
// texte propres deviennent illisibles et doivent céder la place au blanc.
const TrainingCourseSelect = styled(FormSelect)`
  .select__option--is-selected {
    ${TrainingCourseOptionCode}, ${TrainingCourseOptionDescription} {
      color: white;
    }
  }
`;

function formatTrainingCourseOptionLabel({ code, name, price, description }) {
  return (
    <div>
      <div>
        <TrainingCourseOptionCode>[{code}]</TrainingCourseOptionCode> {name}
      </div>
      <TrainingCourseOptionDescription>
        {!!description && `${description} - `}
        <strong>{formatCurrency(getAmountExclTax(price), true)}</strong>
      </TrainingCourseOptionDescription>
    </div>
  );
}

function getTrainingCourseOptionValue(trainingCourse) {
  return trainingCourse.id;
}

// La recherche porte sur le nom et le code de la formation
function filterTrainingCourseOption({ data: trainingCourse }, search) {
  const normalizedSearch = search.trim().toLowerCase();
  return (
    trainingCourse.code.toLowerCase().includes(normalizedSearch) ||
    trainingCourse.name.toLowerCase().includes(normalizedSearch)
  );
}

// Le bon ne peut pas excéder le budget disponible : au-delà, le complément est
// facturé à l'installateur en dehors de la plateforme. C'est ce montant, et non
// celui saisi, qui est annoncé à l'utilisateur puis débité.
function getDebitedAmount(amount, maxAmount) {
  return Math.min(Number(amount) || 0, maxAmount);
}

// Le prix du catalogue s'entend par stagiaire.
function getTrainingCourseAmount(trainingCourse, numberOfTrainees) {
  return roundToCents(
    (getAmountExclTax(trainingCourse.price) || 0) * numberOfTrainees,
  );
}

// La quantité n'est rappelée dans la référence qu'au-delà d'un stagiaire.
function buildTrainingCourseReference(trainingCourse, numberOfTrainees) {
  const designation = [trainingCourse.code, trainingCourse.name]
    .filter(Boolean)
    .join(" - ");

  return numberOfTrainees > 1
    ? `${numberOfTrainees} × ${designation}`
    : designation;
}

const DEFAULT_VALUES = {
  amount: 0,
  reference: "",
  numberOfTrainees: 1,
};

// Ni le montant ni le nombre de stagiaires ne sont validés : un montant
// supérieur au budget reste commandable, il est simplement plafonné à la
// soumission, et un nombre de stagiaires vidé ne doit rien reprocher.
function useTrainingVoucherForm() {
  return useForm({
    mode: "onBlur",
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(
      Yup.object().shape({
        amount: Yup.number().label("Montant"),
        reference: Yup.string().label("Référence").nullable(),
      }),
    ),
  });
}

export default function TrainingVoucherCreate() {
  const { company } = useSelfMetaData();

  const budgetTraining = company?.budgets?.formations;

  const topScroll = useRef();
  const scrollToFormTop = useCallback(() => {
    if (!topScroll) return;
    topScroll.current.scrollIntoView();
  }, [topScroll]);

  const trainingVoucherAction = useTrainingVoucherCreate();

  const [trainingCourses] = useTrainingCourses();

  const [success, setSuccess] = useState(false);
  const [voucher, setVoucher] = useState(null);
  const [selectedTrainingCourse, setSelectedTrainingCourse] = useState(null);

  const form = useTrainingVoucherForm();
  const {
    reset,
    register,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = form;

  const maxAmount = budgetTraining?.totalBalance?.amount || 0;
  const amount = watch("amount", 0);
  const debitedAmount = getDebitedAmount(amount, maxAmount);
  const isAmountOverBudget = Number(amount) > maxAmount;
  const amountOverBudgetMessage = selectedTrainingCourse
    ? `Vous n'avez pas assez de budget pour commander cette formation (${formatCurrency(
        maxAmount,
      )})`
    : `Le montant ne peut pas dépasser votre budget (${formatCurrency(
        maxAmount,
      )})`;

  // Montant et référence sont deux dérivées du même couple (formation, nombre
  // de stagiaires) : les écrire ensemble est ce qui les empêche de diverger.
  const applyTrainingCourse = useCallback(
    (trainingCourse, numberOfTrainees) => {
      if (!trainingCourse) return;

      setValue(
        "amount",
        getTrainingCourseAmount(trainingCourse, numberOfTrainees),
      );
      setValue(
        "reference",
        buildTrainingCourseReference(trainingCourse, numberOfTrainees),
      );
    },
    [setValue],
  );

  // Une formation sélectionnée écrase montant et référence ; en resélectionner
  // une autre écrase à nouveau ces deux valeurs, sans toucher au reste du formulaire.
  const handleSelectTrainingCourse = useCallback(
    (trainingCourseId) => {
      const trainingCourse =
        (trainingCourses || []).find(({ id }) => id === trainingCourseId) ||
        null;
      setSelectedTrainingCourse(trainingCourse);

      applyTrainingCourse(
        trainingCourse,
        Number(getValues("numberOfTrainees")) || 1,
      );
    },
    [trainingCourses, getValues, applyTrainingCourse],
  );

  // Une quantité vidée ou illisible est ignorée : le montant reste en l'état.
  const handleNumberOfTraineesChange = useCallback(
    (event) => {
      const numberOfTrainees = Number(event.target.value);
      if (!numberOfTrainees) return;

      applyTrainingCourse(selectedTrainingCourse, numberOfTrainees);
    },
    [selectedTrainingCourse, applyTrainingCourse],
  );

  const [onRequest, { loading: loadingTrainingVoucher }] = useFormAction(
    form,
    trainingVoucherAction,
    {
      transformData: (data) => ({
        ..._pick(data, ["reference", "date"]),
        amount: getDebitedAmount(data.amount, maxAmount),
      }),
      onError: (err, setError) => {
        setError(
          err?.path || "_global",
          err || {
            message:
              "Votre bon d'achat formation n'a pas pu être créé. Veuillez réessayer plus tard.",
          },
        );
      },
      onSuccess: (res) => {
        setSuccess(true);
        setVoucher(res);
        setSelectedTrainingCourse(null);
        reset(DEFAULT_VALUES);
        scrollToFormTop();
      },
    },
  );

  if (!company || budgetTraining?.totalBalance?.amount <= 0)
    return <Redirect to={paths.trainingVouchers} />;

  return (
    <LoggedInTemplate title="Bons d'achat formation">
      <ImageHeader
        image={header}
        icon="school-line"
        label="Bons d'achat formation"
      />
      <MainContent>
        <Headline title="Créer un bon d'achat formation" />
        <Container>
          <BudgetHeader budget={budgetTraining} budgetButton={null} />
          <Row>
            <Col kind="aside">
              <Panel>
                <GreyContent>
                  <p>
                    Créez votre bon d'achat formation afin d'utiliser votre
                    budget. Il sera déduit automatiquement de vos factures.
                  </p>
                  <p>
                    <strong>Étape 1</strong> - Je clique sur « Commander une
                    session » pour choisir ma session sur l'espace formation
                    Daikin
                    <br />
                    <strong>Étape 2</strong> - Je commande ma session de
                    formation
                    <br />
                    <strong>Étape 3</strong> - Je retourne sur ELITE pour créer
                    mon bon d'achat au montant souhaité (Ce montant viendra en
                    déduction de votre commande)
                    <br />
                    <strong>Étape 4</strong> - Ma session est commandée et mon
                    budget automatiquement décompté
                  </p>
                  <center>
                    <Button to={paths.trainingVouchers} ghost>
                      Retour à la liste des bons
                    </Button>
                  </center>
                </GreyContent>
              </Panel>
            </Col>
            <Col kind="main">
              <ScrollRef ref={topScroll} />

              <Form onSubmit={onRequest} loading={loadingTrainingVoucher}>
                <Panel>
                  {success ? (
                    <div>
                      <p>
                        <strong>Votre bon d'achat formation a été créé</strong>.
                        Il sera déduit automatiquement de vos factures.
                      </p>
                    </div>
                  ) : (
                    <>
                      <PanelTitle>Votre bon d'achat formation</PanelTitle>
                      <PanelSubtitle>
                        Le montant ne peut être supérieur à votre solde de bonus
                        de{" "}
                        {formatCurrency(
                          budgetTraining?.totalBalance?.amount || 0,
                        )}
                        .
                      </PanelSubtitle>
                      <hr />

                      {!!trainingCourses && !!trainingCourses.length && (
                        <Row>
                          <Col span={9}>
                            <FormField label="Formation" layout="column">
                              <TrainingCourseSelect
                                value={selectedTrainingCourse?.id || null}
                                onChange={handleSelectTrainingCourse}
                                options={trainingCourses || []}
                                getOptionValue={getTrainingCourseOptionValue}
                                formatOptionLabel={
                                  formatTrainingCourseOptionLabel
                                }
                                filterOption={filterTrainingCourseOption}
                                isClearable
                                placeholder="Choisissez une formation pour pré-remplir le bon..."
                              />
                            </FormField>
                          </Col>
                          <Col span={3}>
                            <FormField label="Nbre" name="numberOfTrainees">
                              {({ name, ...childProps }) => (
                                <FormInput
                                  type="number"
                                  min={1}
                                  step={1}
                                  {...register(name, {
                                    onChange: handleNumberOfTraineesChange,
                                  })}
                                  {...childProps}
                                />
                              )}
                            </FormField>
                          </Col>
                        </Row>
                      )}

                      <FormField
                        label="Référence"
                        name="reference"
                        errors={errors}
                      >
                        {({ name, ...childProps }) => (
                          <FormInput
                            placeholder="Nom et référence de la formation commandée"
                            {...register(name)}
                            {...childProps}
                          />
                        )}
                      </FormField>
                      <FormField
                        label="Montant"
                        name="amount"
                        errors={errors}
                        error={
                          isAmountOverBudget ? amountOverBudgetMessage : null
                        }
                        layout="column"
                      >
                        {({ name, ...childProps }) => (
                          <span>
                            <span>
                              <FormInput
                                type="number"
                                {...register(name)}
                                {...childProps}
                                style={{ width: "auto", maxWidth: 200 }}
                              />
                            </span>
                            <FormInputAppend>€</FormInputAppend>
                          </span>
                        )}
                      </FormField>
                      <FormField label="Date(s)" name="date" errors={errors}>
                        {({ name, ...childProps }) => (
                          <FormInput
                            placeholder="Date(s) de la formation"
                            {...register(name)}
                            {...childProps}
                          />
                        )}
                      </FormField>
                      <FormField
                        error={!!errors?.accept}
                        errors={errors}
                        name="accept"
                        required
                      >
                        {({ name, ...childProps }) => (
                          <FormCheckbox {...register(name)} {...childProps}>
                            J'accepte de décompter mon budget formation de{" "}
                            {formatCurrency(debitedAmount)}
                          </FormCheckbox>
                        )}
                      </FormField>
                      {!!isAmountOverBudget && (
                        <AmountCapMessage>
                          Le montant ne peut pas être supérieur à{" "}
                          {formatCurrency(maxAmount)}, le complément vous sera
                          facturé directement.
                        </AmountCapMessage>
                      )}
                    </>
                  )}
                </Panel>
                {Boolean(!success) && (
                  <Buttons>
                    <Button
                      type="submit"
                      disabled={budgetTraining?.totalBalance?.amount <= 0}
                    >
                      Je créé mon bon
                    </Button>
                  </Buttons>
                )}
              </Form>
            </Col>
          </Row>
        </Container>
      </MainContent>
    </LoggedInTemplate>
  );
}
